import { useCallback } from 'react'
import en from './locales/en.json'
import { useLocaleStore } from './locale-store'
import type { TranslationKey, TranslationParams, TranslationResources } from './types'

/** Bundled English: shown until the backend's catalogue arrives and for any key it lacks. */
const FALLBACK: TranslationResources = en

// `{name}` → the matching param. An unmatched placeholder is left as-is so it is visible.
function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  )
}

/** Served text → bundled English → the key itself. */
export function resolveText(
  resources: TranslationResources,
  key: string,
  params?: TranslationParams,
): string {
  return interpolate(resources[key] ?? FALLBACK[key] ?? key, params)
}

/**
 * Translate outside React. Reads the store at call time and does not subscribe: a component
 * that must re-render on a language switch uses `useT` instead.
 */
export function translate(key: TranslationKey, params?: TranslationParams): string {
  return resolveText(useLocaleStore.getState().resources, key, params)
}

/** Translate inside a component, re-rendering when the catalogue changes. */
export function useT() {
  const resources = useLocaleStore((state) => state.resources)
  return useCallback(
    (key: TranslationKey, params?: TranslationParams) => resolveText(resources, key, params),
    [resources],
  )
}
